import _ from 'lodash'
import * as THREE from 'three'

import { EditorUserError } from '@glass-designer/utils/editorErrors'
import { UserError } from '@/utils/errors'
import BaseTool from '@glass-designer/tools/baseTool'
import EditingCutout from '@glass-designer/objects/editingCutout'
import RectangleHole from '@glass-designer/model/holes/rectangleHole'
import CircleHole from '@glass-designer/model/holes/circleHole'
import OvalHole from '@glass-designer/model/holes/ovalHole'
import CustomHole from '@glass-designer/model/holes/customHole'
import DatumLink from '@glass-designer/model/datumLink'

import EditorClipper from '@glass-designer/utils/editorClipper'
import MathsUtils from '@/utils/mathsUtils'
import EditorDatumUtils from '@glass-designer/utils/editorDatumUtils'

import GlassDesignerTypes from '@glass-designer/types'


/**
 * Edge Add Cutout tool
 */
export default class AddEdgeCutoutTool extends BaseTool {
  constructor(editor) {
    super(editor)

    this.type = GlassDesignerTypes.ToolTypes.addEdgeCutout
    this.draggingCutoutActive = false
    this.holeObject = null
    this.selectedShape = null
    this.selectedSegment = null

    this.centerCutoutInfoList = []

    // Defaults
    this.cutoutInfo = {
      shapeType: GlassDesignerTypes.LibraryItemShapeTypes.rectangle,
      width: 100,
      height: 100,
      cornerRadius: 5,
      radius: 50,
      inset: 0,
      cutoutMeasureType: GlassDesignerTypes.CutoutMeasurementTypes.center,
      autoRotate: false,
      polished: false
    }

    this.editingDatumLinkId = null

    this.cutoutEditDimRestorePosition = new THREE.Vector2()
    this.cutoutPosition = new THREE.Vector2()
    this.cutoutDirection = new THREE.Vector2()      // Matches the segment in direction
    this.cutoutRotationDir = new THREE.Vector2()    // Matches the segment in / axis align direction (based on auto rotate)

    this.datumLinks = []

    this.cutoutObject = new EditingCutout(editor)
    this.cutoutObject.setVisible(false)
    this.addChildObject(this.cutoutObject)
  }

  // Tool Active
  // Called when the tool is active
  onToolActive() {
    super.onToolActive()

    // Reset
    this.centerCutoutInfoList = []
    this.draggingCutoutActive = false
    this.holeObject = null
    this.selectedShape = null
    this.selectedSegment = null
    this.cutoutObject.setVisible(false)

    this.editor.setDimensionsEnabled([GlassDesignerTypes.DimensionTypes.editingCutoutDimension])
    this.editor.setShapeSelectionEnabled(false)
  }

  // Tool Inactive
  // Called when the tool is no longer active
  onToolInactive() {
    super.onToolInactive()

    this.centerCutoutInfoList.forEach(holeInfo => {
      this.editor.removeSceneObject(holeInfo.holeObject)
      holeInfo.holeObject.dispose()
    })
    this.centerCutoutInfoList = []

    if(this.holeObject) {
      this.holeObject.dispose()
      this.holeObject = null
    }

    this.selectedShape = null
    this.selectedSegment = null

    this.draggingCutoutActive = false
    this.cutoutObject.setVisible(false)

    this.editor.resetDimensionsEnabled()
    this.editor.resetSelectionEnabled()
  }

  // onMouseMoveEvent
  onMouseMoveEvent(ev, mouseData) {
    super.onMouseMoveEvent(ev, mouseData)

    if(this.draggingCutoutActive) {
      var castRay = mouseData.raycaster

      if(this.editor.getOptionSnapGridEnabled()) {
        castRay = this.editor.calculateSnappedRaycast(mouseData.position)
      }

      // Create a plane for the cutout
      // Assumes the editing shape is sitting on the plane at origin (Z = 0)
      var editingPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
      var point = new THREE.Vector3()
      if(castRay.ray.intersectPlane(editingPlane, point)) {
        this.selectedShape = this.editor.findClosestGlassShape(point, { ignoreStockLinked: true, ignoreSegmentListShapeUneditable: true })
        if(this.selectedShape) {

          // Find nearest segment on the shape to snap too
          // Adjust position to snap to the segment
          let closestSegmentIndex = this.selectedShape.calculateClosestSegmentToPoint(point)
          if(closestSegmentIndex != -1) {
            var prevSegmentId = this.selectedSegment ? this.selectedSegment.id : null
            
            this.selectedSegment = this.selectedShape.getSegmentForIndex(closestSegmentIndex)
            var segmentPoint = this.selectedSegment.projectPointOnSegment(point)
            var segmentDir = null

            if(this.selectedSegment.type == GlassDesignerTypes.SegmentTypes.line) {
              segmentDir = this.selectedSegment.getOutDirection()

              // Get length from segment start, round to nearest mm and apply to the position
              let dist = Math.round(segmentPoint.distanceTo(this.selectedSegment.startPosition))
              segmentPoint.set(this.selectedSegment.startPosition.x, this.selectedSegment.startPosition.y)
              segmentPoint.addScaledVector(this.selectedSegment.getDirection(), dist)
            }

            if(this.selectedSegment.type == GlassDesignerTypes.SegmentTypes.arc) {
              var arcAngle = MathsUtils.calculateArcPointAngle(this.selectedSegment.arcCenterPosition, segmentPoint)
              if(!this.selectedSegment.arcClockwise)
                arcAngle += 180
              segmentDir = MathsUtils.generate2DDirectionVector(MathsUtils.degreesToRadians(arcAngle))
            }

            this.cutoutPosition.set(segmentPoint.x, segmentPoint.y)
            this.cutoutDirection.set(-segmentDir.x, -segmentDir.y)

            this._updateCutoutObjectPosition()

            // Adjust for axis aligned rotation so the height is the maximum size
            // If segment info has changed, rebuild the hole
            if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
              if(!this.cutoutInfo.autoRotate) {
                if(prevSegmentId != this.selectedSegment.id) {
                  let gradientDist = this._calculateRectangleAxisAlignedGap()
                  this.holeObject.height = Math.max(this.cutoutInfo.height - gradientDist, 1)
                  this.holeObject.rebuildHoleShape()
                  this.cutoutObject.buildFromHole(this.holeObject)
                }
              }
            }

            // Calculate the 2 datum reference objects in the shape
            // Each edge of the segment
            this._calculateCutoutDatumLinks(this.selectedShape, this.selectedSegment, this.cutoutPosition)

            this.cutoutObject.setDatumLinks(this.datumLinks)
          }

          this.cutoutObject.setVisible(true)

          // Update the dimensions for the cutout to the shape
          this.editor.onToolEditingUpdated(this.selectedShape, [this.cutoutObject])

          this.editor.flagRenderRequired()
        }
      }
    }
  }

  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData) {
    super.onMouseUpEvent(ev, mouseData)

    if(this.draggingCutoutActive) {
      this.draggingCutoutActive = false

      // If no nearby shape available, stop the tool
      if(!this.selectedShape) {
        this.editor.component.errorMessage('No valid editable panel found near the cutout')
        this.editor.cancelActiveTool()
        return
      }

      // Apply corner radiuses based on the selected shape guidelines if the cutout is a standard rectangle
      // Ignored for circles / custom cutouts
      this._applyCutoutGuidelines()

      this.displayToolSettingsPopover()
    }
  }


  /**
   * Display the tool settings popover for the add cutout tool
   */
  displayToolSettingsPopover() {

    // Display the editing cutout popover
    this.editor.component.displayEdgeCutoutToolPopover({
      title: 'Add Edge Cutout Tool',
      shapeType: this.cutoutInfo.shapeType,
      radius: this.cutoutInfo.radius,
      cornerRadius: this.cutoutInfo.cornerRadius,
      inset: this.cutoutInfo.inset,
      width: this.cutoutInfo.width,
      height: this.cutoutInfo.height,
      cutoutMeasureType: this.cutoutInfo.cutoutMeasureType,
      autoRotate: this.cutoutInfo.autoRotate,
      polished: this.cutoutInfo.polished,
      context: this,
      cancelCallback: (ctx, userCancelled) => {
        if(userCancelled)
          ctx.editor.cancelActiveTool()
      },
      applyCallback: (ctx, info) => {
        this._updateCutout(info)

        // Apply cutout to shape
        this._applyEdgeCutout()
      },
      updateCallback: (ctx, info) => {
        this._updateCutout(info)

        ctx.editor.render()
      }
    })
  }


  /**
   * A library item was dragged into the editor while this tool was active
   */
  libraryItemDraggedToEditor(itemInfo) {
    this.selectedShape = null
    this.selectedSegment = null
    this.draggingCutoutActive = true

    this.datumLinks = []
    this.holeObject = null
    
    var cutoutData = {}
    var centerCutoutList = []

    if(itemInfo.itemData) {
      if(_.isArray(itemInfo.itemData.cutoutList) && itemInfo.itemData.cutoutList.length > 0) 
        cutoutData = itemInfo.itemData.cutoutList[0].cutoutData
      if(_.isArray(itemInfo.itemData.centerCutoutList))
        centerCutoutList = itemInfo.itemData.centerCutoutList
    }
    
    // Rectangle / Circle / Custom
    this.cutoutInfo = {
      shapeType: cutoutData.shapeType,
      width: parseInt(cutoutData.width || 100),
      height: parseInt(cutoutData.height || 50),
      cornerRadius: parseFloat(cutoutData.cornerRadius || 5),
      radius: parseFloat(cutoutData.radius || 10),
      inset: parseInt(cutoutData.inset || 0),
      cutoutMeasureType: GlassDesignerTypes.CutoutMeasurementTypes.center,
      offsetHorizontalType: GlassDesignerTypes.HoleMeasurePointTypes.center,
      offsetVerticalType: GlassDesignerTypes.HoleMeasurePointTypes.top,
      autoRotate: false,
      polished: false
    }

    if(cutoutData.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
      var height = this.cutoutInfo.height

      this.holeObject = new RectangleHole(this.editor, { width: this.cutoutInfo.width, height: height, cornerRadius: this.cutoutInfo.cornerRadius })

      // Clear top corners
      this.holeObject.cornerRadiusTL = 0
      this.holeObject.cornerRadiusTR = 0
      this.holeObject.rebuildHoleShape()

    } else if(cutoutData.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.circle) {
      this.cutoutInfo.offsetVerticalType = GlassDesignerTypes.HoleMeasurePointTypes.middle
      this.holeObject = new CircleHole(this.editor, { radius: this.cutoutInfo.radius })

    } else if(cutoutData.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.custom) {
      // Must auto-rotate for custom shapes
      this.cutoutInfo.autoRotate = true

      this.holeObject = new CustomHole(this.editor, {
        segmentList: cutoutData.segmentList,
        isOpenShape: true
      })
    }

    switch(this.cutoutInfo.shapeType) {
      case GlassDesignerTypes.LibraryItemShapeTypes.circle:
        this.holeObject.setAnchorInfo(GlassDesignerTypes.HoleMeasurePointTypes.center, GlassDesignerTypes.HoleMeasurePointTypes.middle)
        break
      case GlassDesignerTypes.LibraryItemShapeTypes.rectangle:
      case GlassDesignerTypes.LibraryItemShapeTypes.custom:
        this.holeObject.setAnchorInfo(GlassDesignerTypes.HoleMeasurePointTypes.center, GlassDesignerTypes.HoleMeasurePointTypes.top)
        break        
    }

    this.holeObject.setVisible(false)
    this.cutoutObject.buildFromHole(this.holeObject)
    this.cutoutObject.setVisible(false)

    // Add in the optional center cutouts associated with the shape cutout
    this.centerCutoutInfoList = []
    this.buildCenterCutoutList(centerCutoutList)

    this.editor.flagRenderRequired()
  }


  /**
   * Build the list of center cutouts associated with the corner cutout
   */
  buildCenterCutoutList(centerCutoutList) {
    // Support list of hole objects
    centerCutoutList.forEach(cutoutInfo => {
      var cutoutData = cutoutInfo.cutoutData
      if(!cutoutData)
        return

      var holeObject = null
      
      // Rectangle / Circle
      if(cutoutData.holeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
         holeObject = new RectangleHole(this.editor, {
          width: parseInt(cutoutData.width || 100),
          height: parseInt(cutoutData.height || 50),
          cornerRadiusTL: parseFloat(cutoutData.cornerRadiusTL || 5),
          cornerRadiusTR: parseFloat(cutoutData.cornerRadiusTR || 5),
          cornerRadiusBR: parseFloat(cutoutData.cornerRadiusBR || 5),
          cornerRadiusBL: parseFloat(cutoutData.cornerRadiusBL || 5),
          angle: parseInt(cutoutData.angle || 0),
          isCutoutPolished: !!cutoutData.isCutoutPolished
        })
      } else if(cutoutData.holeType == GlassDesignerTypes.LibraryItemShapeTypes.circle) {
        holeObject = new CircleHole(this.editor, {
          radius: parseFloat(cutoutData.radius || 5),
          isCutoutPolished: !!cutoutData.isCutoutPolished,
          isCountersunk: !!cutoutData.isCountersunk,
          countersunkRadius: parseFloat(cutoutData.countersunkRadius || 0)
        })
      } else if(cutoutData.holeType == GlassDesignerTypes.LibraryItemShapeTypes.oval) {
        holeObject = new OvalHole(this.editor, {
          width: parseInt(cutoutData.width || 5),
          height: parseInt(cutoutData.height || 5),
          isCutoutPolished: !!cutoutData.isCutoutPolished
        })
      } else {
        // Unknown cutout type
        return
      }

      if(holeObject) {
        holeObject.setVisible(false)
        holeObject.setAnchorInfo(cutoutData.measureToHorizontal, cutoutData.measureToVertical)

        var offset = new THREE.Vector2(0, 0)
        if(cutoutInfo.offset)
          offset.set(parseFloat(cutoutInfo.offset.x || 0), parseFloat(cutoutInfo.offset.y || 0))

        // Center cutouts bundled in the custom cutout are offset from 1st segment point
        // Edge cutouts are measured to center point, so offset by 50% of the cutout width
        if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.custom) {
          let holeRotate = this.holeObject.openingAngle || 0

          // Rotate holes around based on how the custom shape was rotated
          // The CustomHole for edge shapes is rotated to ensure the openings are always on top
          let cutoutMatrix = new THREE.Matrix4()
          let objAngles = new THREE.Euler(0, 0, MathsUtils.degreesToRadians(holeRotate))
          cutoutMatrix.makeRotationFromEuler(objAngles)
          let rotateMatrix = new THREE.Matrix3()
          rotateMatrix.setFromMatrix4(cutoutMatrix)
          offset = offset.applyMatrix3(rotateMatrix)

          offset.x += this.holeObject.anchorPoint.x
          offset.y += this.holeObject.anchorPoint.y
        }

        this.centerCutoutInfoList.push({
          holeObject: holeObject,
          offset: offset
        })

        this.cutoutObject.addHoleWithOffset(holeObject, offset)
      }
    })
  }


  /**
   * Update the cutout from the popover info
   */
  _updateCutout(updateData) {
    this.cutoutInfo.width = parseInt(updateData.width)
    this.cutoutInfo.height = parseInt(updateData.height)
    this.cutoutInfo.cornerRadius = parseFloat(updateData.cornerRadius)
    this.cutoutInfo.radius = parseInt(updateData.radius)
    this.cutoutInfo.inset = parseInt(updateData.inset)
    this.cutoutInfo.cutoutMeasureType = updateData.cutoutMeasureType
    this.cutoutInfo.autoRotate = updateData.autoRotate
    this.cutoutInfo.polished = updateData.polished

    if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
      this.holeObject.width = this.cutoutInfo.width
      this.holeObject.height = this.cutoutInfo.height

      // Adjust for axis aligned rotation so the height is the maximum size
      if(!this.cutoutInfo.autoRotate) {
        let gradientDist = this._calculateRectangleAxisAlignedGap()
        this.holeObject.height -= gradientDist
        this.holeObject.height = Math.max(this.holeObject.height, 1)
      }

      this.holeObject.cornerRadiusBL = this.cutoutInfo.cornerRadius
      this.holeObject.cornerRadiusBR = this.cutoutInfo.cornerRadius
      this.holeObject.cornerRadiusRL = 0
      this.holeObject.cornerRadiusRR = 0
      this.holeObject.rebuildHoleShape()
    }

    if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.circle) {
      this.holeObject.radius = this.cutoutInfo.radius
      this.holeObject.rebuildHoleShape()
    }

    // Update position to account for inset changes
    this._updateCutoutObjectPosition()

    this._calculateCutoutDatumLinks(this.selectedShape, this.selectedSegment, this.cutoutPosition)
    this.cutoutObject.setDatumLinks(this.datumLinks)

    // Recalc dimensions
    this.editor.onToolEditingUpdated(this.selectedShape, [this.cutoutObject])
  }


  /**
   * Update the cutout object position/rotate
   */
  _updateCutoutObjectPosition() {

    
    // If auto-rotate is disabled, snap rotation to closest axis
    this.cutoutRotationDir.copy(this.cutoutDirection)
    if(!this.cutoutInfo.autoRotate) {
      if(Math.abs(this.cutoutDirection.x) > Math.abs(this.cutoutDirection.y))
        this.cutoutRotationDir.y = 0
      else
        this.cutoutRotationDir.x = 0
    }

    // If datum links are fixed, move the center position of the cutout
    if(this.cutoutInfo.cutoutMeasureType == GlassDesignerTypes.CutoutMeasurementTypes.edge && this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
      var halfWidth = this._calculateCutoutWidth() / 2.0
      let segment = this.selectedShape.getSegmentById(this.datumLinks[0].segmentId)

      if(segment && this.datumLinks[0].isFixedDatumLink) {
        this.cutoutPosition.copy(segment.startPosition)
        this.cutoutPosition.addScaledVector(segment.getDirection(), this.datumLinks[0].sourceOffsetDistance + halfWidth)
      }
      if(segment && this.datumLinks[1].isFixedDatumLink) {
        this.cutoutPosition.copy(segment.endPosition)
        this.cutoutPosition.addScaledVector(segment.getDirection(), -(this.datumLinks[1].sourceOffsetDistance + halfWidth))
      }
    }
    

    var cutoutPos = new THREE.Vector2(this.cutoutPosition.x, this.cutoutPosition.y)
    var angle = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(this.cutoutRotationDir)) - 90)

    // Add inset for circle edge cutouts
    if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.circle) {
      cutoutPos.addScaledVector(this.cutoutDirection, this.cutoutInfo.inset)

      // Don't rotate circles. No need
      angle = 0
    }

    // Rebuild if rectangle/circle
    if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle || this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.circle) {
      this.holeObject.setAnchorInfo(this.cutoutInfo.offsetHorizontalType, this.cutoutInfo.offsetVerticalType)
      this.cutoutObject.buildFromHole(this.holeObject)
    }

    this.cutoutObject.setPosition(cutoutPos)
    this.cutoutObject.setRotation(angle)
  }



  /**
   * Apply the edge cutout using the current position/angle/hole details
   * Applies to the last set shape while editing
   */
  _applyEdgeCutout() {
    this.draggingCutoutActive = false
    this.cutoutObject.setVisible(false)

    try {
      var resultSegmentList = []
      let groupStartPosition = null
      let groupEndPosition = null

      let subjectSegmentList = this.selectedShape.segmentList
      var clipSegmentList = this.cutoutObject.generateSegmentList()

      // For custom, we don't using clipping, we just use the entry/exist points of the shape & insert it into the glass panel
      if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.custom) {

        let clipResult = MathsUtils.insertCustomOpenShapeToShape(subjectSegmentList, clipSegmentList)

        resultSegmentList = clipResult.segmentList
        groupStartPosition = clipResult.startPosition.clone()
        groupEndPosition = clipResult.endPosition.clone()
      }

      // Apply an offset for the rectangle cutout to not be coplanar with the glass shape
      if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
        // Need to calculate the gradient of the segment
        // Add in a buffer just to go over the edge
        var outsideClipSize = 4

        this.holeObject.height = this.cutoutInfo.height

        if(!this.cutoutInfo.autoRotate) {
          let gradientDist = this._calculateRectangleAxisAlignedGap()

          outsideClipSize += Math.ceil(gradientDist * 2.5)   // Add buffer to make sure it's outside
          this.holeObject.height -= gradientDist
          this.holeObject.height = Math.max(this.holeObject.height, 1)
        }

        this.holeObject.height += outsideClipSize / 2

        this.holeObject.rebuildHoleShape()
        this.holeObject.setAnchorInfo(GlassDesignerTypes.HoleMeasurePointTypes.center, GlassDesignerTypes.HoleMeasurePointTypes.top)

        var cutoutPos = new THREE.Vector2(this.cutoutPosition.x, this.cutoutPosition.y)
        cutoutPos.addScaledVector(this.cutoutRotationDir, -outsideClipSize / 2)
        
        this.cutoutObject.setPosition(cutoutPos)
        this.cutoutObject.updateFromHole(this.holeObject)

        clipSegmentList = this.cutoutObject.generateSegmentList()
      }

      // Run the clipping tool for non-custom shapes
      if(this.cutoutInfo.shapeType != GlassDesignerTypes.LibraryItemShapeTypes.custom) {
        
        let clipper = new EditorClipper()
        let clipResult = clipper.performSegmentListClippingOperation(subjectSegmentList, clipSegmentList, false)
        resultSegmentList = clipResult.segmentList

        // Setup group info for cutout groups
        groupStartPosition = clipResult.startPosition.clone()
        groupEndPosition = clipResult.endPosition.clone()
      }

      if(resultSegmentList.length == 0) {
        throw new EditorUserError('Cutting shape does not intersect glass')
      }

      // Reset dimensions for editing
      this.editor.dimensionHelper.resetEditingDimensions()

      this.editor.startUndoGroup('Adding Edge Cutout')
      this.editor.addUndoShape(this.selectedShape)
      this.selectedShape.segmentList = resultSegmentList

      // Setup matrix for the center cutouts
      let angle = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(this.cutoutDirection)) - 90)

      let cutoutMatrix = new THREE.Matrix4()
      let objAngles = new THREE.Euler(0, 0, MathsUtils.degreesToRadians(angle))
      cutoutMatrix.makeRotationFromEuler(objAngles)
      cutoutMatrix.setPosition(new THREE.Vector3(this.cutoutPosition.x, this.cutoutPosition.y, 0))


      // Add in the center cutouts
      this.centerCutoutInfoList.forEach(holeInfo => {
        // Calculate datum links for each hole with offset adjusted
        var holeDatumLinks = [
          new DatumLink(),
          new DatumLink()
        ]

        // Need to transform hole position by the cutout object correctly
        // Also flip angle if rectangle if scale flipped
        let holePosition = new THREE.Vector3(holeInfo.offset.x, holeInfo.offset.y, 0)
        holePosition = holePosition.applyMatrix4(cutoutMatrix)
        EditorDatumUtils.calculateClosestDatumLinks(this.selectedShape, holeDatumLinks, new THREE.Vector2(holePosition.x, holePosition.y), { ignoreAllHoles: false })

        // Run checks here. If outside shape, flag error
        if(!this.selectedShape.isPointInsideShape(holePosition))
          throw new EditorUserError('Center cutout is outside of glass panel')

        // Rotate / flip center cutout
        holeInfo.holeObject.angle += angle
        holeInfo.holeObject.angle = MathsUtils.wrapAngleDegrees(holeInfo.holeObject.angle)

        // Datum links to segments / other holes
        holeInfo.holeObject.setVisible(true)
        holeInfo.holeObject.setDatumLinks(holeDatumLinks)
        this.selectedShape.addCenterCutout(holeInfo.holeObject)
      })
      // Clear cutout info list, don't release it as it's owned by the shape now
      this.centerCutoutInfoList = []


      // Update cutout groups in the shape
      if(groupStartPosition && groupEndPosition) {
        this.selectedShape.updateCutoutGroup(groupStartPosition, groupEndPosition, {
          cutoutType: GlassDesignerTypes.CutoutTypes.edge,
          cutoutMeasurementType: this.cutoutInfo.cutoutMeasureType,
          polished: this.cutoutInfo.polished
        })
      }

  
      this.editor.closeCurrentUndoGroup()
      this.selectedShape.segmentListUpdated()
      this.selectedShape.updateSceneObject()
      this.editor.onShapeUpdated(this.selectedShape)

      this.closeTool()      
    } catch(err) {

      // Display error (eg, hole outside of shape or whatever)
      if(err instanceof EditorUserError || err instanceof UserError)
        this.editor.component.errorMessage('Error placing edge cutout', err.message)

      console.error(err)
    }    
  }


  /**
   * Calculate the width of the cutout
   */
  _calculateCutoutWidth() {
    switch(this.cutoutInfo.shapeType) {
      case GlassDesignerTypes.LibraryItemShapeTypes.rectangle:
        return this.cutoutInfo.width
      case GlassDesignerTypes.LibraryItemShapeTypes.circle:
        return this.cutoutInfo.radius * 2

      case GlassDesignerTypes.LibraryItemShapeTypes.custom:
        if(this.holeObject) {
          // Source segment list will be rotated by CustomHole to always have open section point Y-
          return this.holeObject.calculateOpenShapeGapWidth()
        }
        return 0

      default:
        return 0
    }
  }


  /**
   * Apply the minimum corner radius info based on guidelines for the selected shape to a standard rectangle/circle cutout
   * Not done for custom cutouts as they are to spec. They'll just show warnings if invalid
   */
  _applyCutoutGuidelines() {
    if(!this.selectedShape)
      return
  
    if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
      let minRadius = this.editor.getGuidelineHelper().calculateMinimumCornerRadiusForThickness(this.selectedShape.getGlassType(), this.selectedShape.getThickness(), 25)    // 25% for 1 corner out of 4

      this.cutoutInfo.cornerRadius = Math.max(this.cutoutInfo.cornerRadius, minRadius)
      this.holeObject.cornerRadiusBL = this.cutoutInfo.cornerRadius
      this.holeObject.cornerRadiusBR = this.cutoutInfo.cornerRadius
      this.holeObject.rebuildHoleShape()
      this.cutoutObject.buildFromHole(this.holeObject)
    }
    if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.circle) {
      let minRadius = this.editor.getGuidelineHelper().calculateMinimumCornerRadiusForThickness(this.selectedShape.getGlassType(), this.selectedShape.getThickness(), 50)    // 50%, assumes circle is half on/off the edge

      this.cutoutInfo.radius = Math.max(this.cutoutInfo.radius, minRadius)
      this.holeObject.radius = this.cutoutInfo.radius
      this.holeObject.rebuildHoleShape()
      this.cutoutObject.buildFromHole(this.holeObject)
    }
  }


  /**
   * Calculate the gap between an axis aligned rectangle & the segment
   * Used to offset the rectangle to cover the gap for segments on an angle
   */
  _calculateRectangleAxisAlignedGap() {
    let gradientAngle = MathsUtils.get2DVectorAngle(this.cutoutDirection)
    let axisAngle = MathsUtils.get2DVectorAngle(this.cutoutRotationDir)

    return Math.abs((this.cutoutInfo.width / 2) * Math.tan(axisAngle - gradientAngle))
  }


  /**
   * Calculate the datum links to point to a cutout along a segment from either of the segments ends 
   * Updates datumLinks array
   */
  _calculateCutoutDatumLinks(shape, segment, position) {
    if(this.datumLinks.length == 0) {
      this.datumLinks.push(new DatumLink())
      this.datumLinks.push(new DatumLink())
    }

    var cutoutEdgePos1 = null
    var cutoutEdgePos2 = null

    let segDir = segment.getDirection()
    var halfWidth = this._calculateCutoutWidth() / 2.0

    switch(this.cutoutInfo.cutoutMeasureType) {
      case GlassDesignerTypes.CutoutMeasurementTypes.center:
        cutoutEdgePos1 = segment.projectPointOnSegment(position, false)
        cutoutEdgePos2 = cutoutEdgePos1
        break

      case GlassDesignerTypes.CutoutMeasurementTypes.edge:
        cutoutEdgePos1 = segment.projectPointOnSegment(position, false)
        cutoutEdgePos2 = cutoutEdgePos1.clone()

        cutoutEdgePos1.addScaledVector(segDir, -halfWidth)
        cutoutEdgePos2.addScaledVector(segDir, halfWidth)
        break
    }
    
    let segStartDistanceAlong = segment.startPosition.distanceTo(cutoutEdgePos1)
    let segEndDistanceAlong = segment.endPosition.distanceTo(cutoutEdgePos2)

    // Update datum links
    this.datumLinks[0].update({
      sourceShapeId: shape.id,
      sourceDatumType: GlassDesignerTypes.DatumTypes.glassSegmentStart,
      sourceOffsetDistance: segStartDistanceAlong,
      sourceOffsetDirection: 1,
      segmentId: segment.id
    })

    this.datumLinks[1].update({
      sourceShapeId: shape.id,
      sourceDatumType: GlassDesignerTypes.DatumTypes.glassSegmentEnd,
      sourceOffsetDistance: segEndDistanceAlong,
      sourceOffsetDirection: 1,
      segmentId: segment.id
    })
  }

  // A dimension used while editing in this tool was clicked
  editingDimensionClicked(dimension) {
    if(dimension.dimType != GlassDesignerTypes.DimensionTypes.editingCutoutDimension)
      return

    this.editingDatumLinkId = dimension.linkData.datumLinkId
    this.cutoutEditDimRestorePosition.set(this.cutoutPosition.x, this.cutoutPosition.y)

    let selectedDatumLink = this.datumLinks.find(datumLink => datumLink.id == dimension.linkData.datumLinkId)
    if(!selectedDatumLink)
      return

    // Allow fixed dimensions for cutouts measuring to edge
    let displayUserFixedField = false
    if(this.cutoutInfo.cutoutMeasureType == GlassDesignerTypes.CutoutMeasurementTypes.edge) {
      if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle)
        displayUserFixedField = true
    }
    
    // Fill in options
    var popoverOptions = {
      value: dimension.getTotalLength(),
      displayUserFixedField: displayUserFixedField,
      allowPlusMinus: false,
      isUserFixed: displayUserFixedField ? selectedDatumLink.isFixedDatumLink : false,
      title: 'Length',
      addon: 'mm',
      context: this,
      updateCallback: this.updateDimensionEdit,
      cancelCallback: this.cancelDimensionEdit,
      applyCallback: this.applyDimensionEdit
    }

    this.editor.component.displayNumberEntryPopover(popoverOptions)
  }

  updateDimensionEdit(context, options) {
    context._updateEditingCutoutFromLinearDimensionEdit(context.selectedShape, context.editingDatumLinkId, options.value, options.isUserFixed)
  }
  applyDimensionEdit(context, options) {
    context._updateEditingCutoutFromLinearDimensionEdit(context.selectedShape, context.editingDatumLinkId, options.value, options.isUserFixed)
  }

  // Update the editing cutout from a linear dimension editing
  _updateEditingCutoutFromLinearDimensionEdit(shape, datumLinkId, newLength, isUserFixed) {
    let datumLink = this.datumLinks.find(datum => datum.id == datumLinkId)
    if(!datumLink)
      return
    let otherDatumLink = this.datumLinks.find(datum => datum.id != datumLinkId)

    let segment = shape.getSegmentById(datumLink.segmentId)
    if(!segment)
      return

    datumLink.isFixedDatumLink = isUserFixed
    let isStart = datumLink.sourceDatumType == GlassDesignerTypes.DatumTypes.glassSegmentStart
    let refPoint = isStart ? segment.startPosition : segment.endPosition
    let refDirection = segment.getDirection().multiplyScalar(isStart ? 1.0 : -1.0)

    // Adjust width if the other datum link is fixed
    // Only available for edge rectangle cutouts
    if(otherDatumLink) {
      if(otherDatumLink.isFixedDatumLink) {
        if(this.cutoutInfo.cutoutMeasureType == GlassDesignerTypes.CutoutMeasurementTypes.edge) {
          if(this.cutoutInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {

            this.cutoutInfo.width = segment.getLength() - newLength - otherDatumLink.sourceOffsetDistance
            this.holeObject.width = this.cutoutInfo.width

            this.holeObject.rebuildHoleShape()
          }
        }
      }        
    }

    var halfWidth = this._calculateCutoutWidth() / 2.0
    
    let newPosition = new THREE.Vector2(refPoint.x, refPoint.y)
    newPosition.addScaledVector(new THREE.Vector2(refDirection.x, refDirection.y), newLength)

    if(this.cutoutInfo.cutoutMeasureType == GlassDesignerTypes.CutoutMeasurementTypes.edge) {
      newPosition.addScaledVector(new THREE.Vector2(refDirection.x, refDirection.y), halfWidth)
    }

    this.cutoutPosition.set(newPosition.x, newPosition.y)

    // Update cutout & datum links
    this._updateCutoutObjectPosition()
    this._calculateCutoutDatumLinks(this.selectedShape, segment, this.cutoutPosition)
    this.cutoutObject.setDatumLinks(this.datumLinks)

    // Recalc dimensions
    this.editor.onToolEditingUpdated(shape, [this.cutoutObject])

    this.editor.render()
  }

  cancelDimensionEdit(context, userCancelled) {
    // Restore from previous value
    if(userCancelled) {
      context.cutoutPosition.set(context.cutoutEditDimRestorePosition.x, context.cutoutEditDimRestorePosition.y)

      // Update cutout & datum links
      context._updateCutoutObjectPosition()
      context._calculateCutoutDatumLinks(context.selectedShape, context.selectedSegment, context.cutoutPosition)
      context.cutoutObject.setDatumLinks(context.datumLinks)
    }

    // Recalc dimensions
    context.editor.onToolEditingUpdated(context.selectedShape, [context.cutoutObject])

    context.editingDatumLinkId = null
    context.displayToolSettingsPopover()
  }
}